import { Fragment, useState } from "react";
import { Combobox as HCombobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/20/solid";

interface ComboboxOption {
  value: number;
  label: string;
}

interface ComboboxProps {
  label?: string;
  error?: string;
  hint?: string;
  options: ComboboxOption[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** If provided, called on every keystroke — enables server-side filtering */
  onQueryChange?: (query: string) => void;
}

export function Combobox({
  label,
  error,
  hint,
  options,
  value,
  onChange,
  placeholder = "Type to search…",
  disabled = false,
  onQueryChange,
}: ComboboxProps) {
  const [query, setQuery] = useState("");

  const inputId = label?.toLowerCase().replace(/\s+/g, "-");

  const selectedOption = options.find((o) => o.value === value) ?? null;

  // Client-side filter unless parent handles it via onQueryChange
  const filtered =
    onQueryChange || query === ""
      ? options
      : options.filter((o) =>
          o.label.toLowerCase().includes(query.toLowerCase())
        );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onQueryChange?.(e.target.value);
    // If user types, clear the current selection
    if (value !== null) onChange(null);
  };

  const handleChange = (option: ComboboxOption | null) => {
    onChange(option?.value ?? null);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setQuery("");
    onQueryChange?.("");
  };

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-stone-700 mb-1.5">
          {label}
        </label>
      )}
      <HCombobox value={selectedOption} onChange={handleChange} disabled={disabled}>
        <div className="relative">
          <div
            className={`
              relative w-full flex items-center rounded-lg border text-sm
              ${error ? "border-red-400 bg-red-50" : "border-stone-300 bg-white"}
              ${disabled ? "bg-stone-50 cursor-not-allowed" : ""}
              focus-within:ring-2 focus-within:ring-offset-0 focus-within:border-transparent
              ${error ? "focus-within:ring-red-400" : "focus-within:ring-forest-500"}
            `}
          >
            <HCombobox.Input
              id={inputId}
              className="w-full bg-transparent pl-3 pr-9 py-2.5 focus:outline-none disabled:cursor-not-allowed disabled:text-stone-400"
              displayValue={(option: ComboboxOption | null) => option?.label ?? query}
              onChange={handleInputChange}
              placeholder={placeholder}
              autoComplete="off"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
              {value !== null && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-0.5 rounded text-stone-400 hover:text-stone-600"
                  tabIndex={-1}
                  aria-label="Clear"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
              <HCombobox.Button className="p-0.5 text-stone-400 hover:text-stone-600">
                <ChevronUpDownIcon className="w-4 h-4" aria-hidden />
              </HCombobox.Button>
            </div>
          </div>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => {
              if (!value) setQuery("");
            }}
          >
            <HCombobox.Options className="absolute z-50 mt-1 w-full overflow-auto rounded-lg bg-white border border-stone-200 shadow-lg max-h-60 py-1 text-sm focus:outline-none">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-stone-400 select-none">No results found.</div>
              ) : (
                filtered.map((option) => (
                  <HCombobox.Option key={option.value} value={option} as={Fragment}>
                    {({ active, selected }) => (
                      <li
                        className={`
                          flex items-center gap-2 px-3 py-2 cursor-pointer select-none
                          ${active ? "bg-forest-50 text-forest-900" : "text-stone-800"}
                        `}
                      >
                        <CheckIcon
                          className={`w-4 h-4 flex-shrink-0 ${selected ? "text-forest-600" : "invisible"}`}
                        />
                        <span className={selected ? "font-medium" : ""}>{option.label}</span>
                      </li>
                    )}
                  </HCombobox.Option>
                ))
              )}
            </HCombobox.Options>
          </Transition>
        </div>
      </HCombobox>
      {hint && !error && <p className="mt-1.5 text-xs text-stone-400">{hint}</p>}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}
